import { ConfidenceIndicator } from "@/components/ConfidenceIndicator";
import { SortDirection, SortField } from "@/hooks/useSort";
import { useTimezone } from "@/hooks/useTimezone";
import { Document } from "@/services/documentsApi";
import { DocumentStatus } from "@/types/document";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { Checkbox, Tooltip } from "@mui/material";

export interface DocumentTableProps {
  /** Documents to display */
  documents: Document[];
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error?: Error | null;
  /** Selected document IDs */
  selectedIds: Set<string>;
  /** Whether all page items are selected */
  isAllSelected: boolean;
  /** IDs of documents that cannot be selected (e.g., processing) */
  disabledIds: Set<string>;
  /** Toggle all items on page */
  onToggleAll: () => void;
  /** Handle row click with selection */
  onRowClick: (id: string, event: React.MouseEvent) => void;
  /** Navigate to document view */
  onViewDocument: (id: string) => void;
  /** Delete single document */
  onDeleteDocument: (id: string, fileName: string) => void;
  /** Whether delete is in progress */
  isDeleting?: boolean;
  /** Current sort field */
  sortField?: SortField;
  /** Current sort direction */
  sortDirection?: SortDirection;
  /** Callback when sort changes */
  onSort?: (field: SortField) => void;
}

/**
 * Get status badge based on document status
 */
function getStatusBadge(status: DocumentStatus) {
  const badges: Record<DocumentStatus, JSX.Element> = {
    completed: (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/50 dark:text-green-300">
        <svg className="mr-1 h-1.5 w-1.5 text-green-500" fill="currentColor" viewBox="0 0 8 8">
          <circle cx="4" cy="4" r="3"></circle>
        </svg>
        Complete
      </span>
    ),
    processing: (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
        <svg
          className="mr-1 h-1.5 w-1.5 animate-pulse text-blue-500"
          fill="currentColor"
          viewBox="0 0 8 8"
        >
          <circle cx="4" cy="4" r="3"></circle>
        </svg>
        Processing
      </span>
    ),
    error: (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/50 dark:text-red-300">
        <svg className="mr-1 h-1.5 w-1.5 text-red-500" fill="currentColor" viewBox="0 0 8 8">
          <circle cx="4" cy="4" r="3"></circle>
        </svg>
        Error
      </span>
    ),
    uploading: (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
        <svg
          className="mr-1 h-1.5 w-1.5 animate-spin text-blue-500"
          fill="currentColor"
          viewBox="0 0 8 8"
        >
          <circle cx="4" cy="4" r="3"></circle>
        </svg>
        Uploading
      </span>
    ),
    uploaded: (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
        <svg className="mr-1 h-1.5 w-1.5 text-blue-500" fill="currentColor" viewBox="0 0 8 8">
          <circle cx="4" cy="4" r="3"></circle>
        </svg>
        Uploaded
      </span>
    ),
    review_required: (
      <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/50 dark:text-orange-300">
        <svg className="mr-1 h-1.5 w-1.5 text-orange-500" fill="currentColor" viewBox="0 0 8 8">
          <circle cx="4" cy="4" r="3"></circle>
        </svg>
        Review
      </span>
    ),
  };
  return badges[status];
}

/**
 * Document table with selection support, compact styling, and checkbox column
 */
export function DocumentTable({
  documents,
  isLoading,
  error,
  selectedIds,
  isAllSelected,
  disabledIds,
  onToggleAll,
  onRowClick,
  onViewDocument,
  onDeleteDocument,
  isDeleting = false,
  sortField,
  sortDirection,
  onSort,
}: DocumentTableProps) {
  const { formatDate, timezoneAbbreviation } = useTimezone();
  const hasSelectableDocuments = documents.some((doc) => !disabledIds.has(doc.id));

  // Sortable header component
  const SortableHeader = ({
    field,
    children,
    className = "",
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => {
    const isActive = sortField === field;
    const handleClick = () => onSort?.(field);

    return (
      <th
        className={`px-3 py-2 text-left text-xs font-medium leading-normal text-gray-700 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-[#243044] select-none transition-colors ${className}`}
        onClick={handleClick}
      >
        <div className="flex items-center gap-1">
          <span>{children}</span>
          {onSort && (
            <span
              className={`inline-flex ${isActive ? "text-primary" : "text-gray-400 dark:text-gray-600"}`}
            >
              {isActive && sortDirection === "asc" ? (
                <ArrowUpward sx={{ fontSize: 14 }} />
              ) : isActive && sortDirection === "desc" ? (
                <ArrowDownward sx={{ fontSize: 14 }} />
              ) : (
                <ArrowDownward sx={{ fontSize: 14, opacity: 0.3 }} />
              )}
            </span>
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="flex overflow-hidden rounded-b-lg border border-gray-200 bg-white dark:border-[#324467] dark:bg-[#111722]">
      <table className="w-full flex-1">
        <thead>
          <tr className="bg-gray-50 dark:bg-[#192233]">
            {/* Checkbox column */}
            <th className="w-9 px-2 py-2 text-left">
              <Checkbox
                checked={isAllSelected && hasSelectableDocuments}
                indeterminate={selectedIds.size > 0 && !isAllSelected}
                onChange={onToggleAll}
                disabled={!hasSelectableDocuments}
                size="small"
                sx={{
                  color: "gray",
                  "&.Mui-checked": { color: "primary.main" },
                  "&.MuiCheckbox-indeterminate": { color: "primary.main" },
                }}
              />
            </th>
            <SortableHeader field="fileName">File Name</SortableHeader>
            <SortableHeader field="proNumber" className="w-32">
              PRO Number
            </SortableHeader>
            <SortableHeader field="documentType" className="w-16">
              Type
            </SortableHeader>
            <SortableHeader field="status" className="w-24">
              Status
            </SortableHeader>
            <SortableHeader field="confidence" className="w-32">
              Confidence
            </SortableHeader>
            <th
              className="w-44 px-3 py-2 text-left text-xs font-medium leading-normal text-gray-700 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-[#243044] select-none transition-colors"
              onClick={() => onSort?.("uploadedAt")}
            >
              <div className="flex items-center gap-1">
                <Tooltip title={`Times shown in ${timezoneAbbreviation}`} arrow placement="top">
                  <span className="cursor-help">
                    Submitted On{" "}
                    <span className="text-gray-400 dark:text-gray-500">
                      ({timezoneAbbreviation})
                    </span>
                  </span>
                </Tooltip>
                {onSort && (
                  <span
                    className={`inline-flex ${sortField === "uploadedAt" ? "text-primary" : "text-gray-400 dark:text-gray-600"}`}
                  >
                    {sortField === "uploadedAt" && sortDirection === "asc" ? (
                      <ArrowUpward sx={{ fontSize: 14 }} />
                    ) : sortField === "uploadedAt" && sortDirection === "desc" ? (
                      <ArrowDownward sx={{ fontSize: 14 }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 14, opacity: 0.3 }} />
                    )}
                  </span>
                )}
              </div>
            </th>
            <th className="w-20 px-3 py-2 text-left text-xs font-medium leading-normal text-gray-700 dark:text-white">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => {
            const isSelected = selectedIds.has(doc.id);
            const isDisabled = disabledIds.has(doc.id);
            const isProcessing =
              doc.status === "processing" ||
              doc.status === "uploading" ||
              doc.status === "uploaded";

            return (
              <tr
                key={doc.id}
                onClick={(e) => !isDisabled && onRowClick(doc.id, e)}
                className={`border-t border-gray-200 dark:border-t-[#324467] cursor-pointer transition-colors ${
                  isSelected
                    ? "bg-primary/10 dark:bg-primary/20"
                    : "hover:bg-gray-50 dark:hover:bg-[#192233]/50"
                } ${isDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {/* Checkbox */}
                <td className="w-9 px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() =>
                      !isDisabled && onRowClick(doc.id, { ctrlKey: true } as React.MouseEvent)
                    }
                    size="small"
                    sx={{
                      color: "gray",
                      "&.Mui-checked": { color: "primary.main" },
                    }}
                  />
                </td>
                {/* File name */}
                <td className="px-3 py-1.5 text-xs font-normal leading-normal text-black dark:text-white truncate max-w-[200px]">
                  {doc.fileName}
                </td>
                {/* PRO Number */}
                <td className="w-32 px-3 py-1.5 text-xs font-normal leading-normal text-gray-600 dark:text-[#92a4c9]">
                  {doc.proNumber || "—"}
                </td>
                {/* Document Type */}
                <td className="w-16 px-3 py-1.5 text-xs font-normal leading-normal text-gray-600 dark:text-[#92a4c9]">
                  {doc.documentType || "—"}
                </td>
                {/* Status */}
                <td className="w-24 px-3 py-1.5 text-xs font-normal leading-normal">
                  {getStatusBadge(doc.status)}
                </td>
                {/* Confidence */}
                <td className="w-32 px-3 py-1.5 text-xs font-normal leading-normal">
                  <ConfidenceIndicator
                    confidence={doc.confidence ?? null}
                    isProcessing={isProcessing}
                    compact
                  />
                </td>
                {/* Submitted on */}
                <td className="w-44 px-3 py-1.5 text-xs font-normal leading-normal text-gray-600 dark:text-[#92a4c9]">
                  {formatDate(doc.uploadedAt)}
                </td>
                {/* Actions */}
                <td className="w-20 px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    {doc.status === "completed" || doc.status === "review_required" ? (
                      <button
                        onClick={() => onViewDocument(doc.id)}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        View
                      </button>
                    ) : doc.status === "error" ? (
                      <button className="text-xs font-medium text-primary hover:underline">
                        Details
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
                    )}
                    <button
                      onClick={() => onDeleteDocument(doc.id, doc.fileName)}
                      disabled={isDeleting}
                      className="ml-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete document"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {isLoading && (
            <tr className="border-t border-gray-200 dark:border-t-[#324467]">
              <td
                className="h-[80px] px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400"
                colSpan={8}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-primary"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading documents...
                </div>
              </td>
            </tr>
          )}
          {error && (
            <tr className="border-t border-gray-200 dark:border-t-[#324467]">
              <td
                className="h-[80px] px-4 py-2 text-center text-sm text-red-600 dark:text-red-400"
                colSpan={8}
              >
                Failed to load documents. Please try again.
              </td>
            </tr>
          )}
          {!isLoading && !error && documents.length === 0 && (
            <tr className="border-t border-gray-200 dark:border-t-[#324467]">
              <td
                className="h-[80px] px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400"
                colSpan={8}
              >
                No documents match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
